import { styled, Box } from "@mui/material";
import { MaterialDesignContent, SnackbarProvider } from "notistack";
import { ReactChildren } from "../../interfaces/Common";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  borderRadius: "12px !important",
  backgroundColor: "#FFFFFF !important",
  fontSize: "16px",
  fontWeight: "500",
  color: "#212121 !important",
  "&.notistack-MuiContent-success": {
    borderBottom: "6px solid #388E3C !important",
    backgroundColor: "#FFFFFF !important",
    "& .notistack-MuiContent-iconWrapper": {
      display: "none !important",
    },
  },
  "&.notistack-MuiContent-error": {
    borderBottom: "6px solid #C02B2B !important",
    backgroundColor: "#FFFFFF !important",
    "& .notistack-MuiContent-iconWrapper": {
      display: "none !important",
    },
  },
  "&.notistack-MuiContent-default": {
    fontSize: "16px",
    fontWeight: "500",
  },
  "&.notistack-MuiContent-warning": {
    fontSize: "16px",
    fontWeight: "500",
  },
  "&.notistack-MuiContent-info": {
    fontSize: "16px",
    fontWeight: "500",
  },
}));


const CustomSuccessContent = React.forwardRef<HTMLDivElement, any>((props, ref) => {
  const { message, ...rest } = props;
  return (
    <Box
      ref={ref}
      sx={{
        borderRadius: "12px",
        backgroundColor: "#FFFFFF",
        borderBottom: "6px solid #388E3C",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        fontSize: "16px",
        fontWeight: 500,
        color: "#212121",
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#C1DCC3",
          borderRadius: "50%",
          padding: "3px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "28px",
          height: "28px",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            backgroundColor: "#388E3C",
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "22px",
            height: "22px",
          }}
        >
          <CheckCircleIcon 
            sx={{ 
              color: "#FFFFFF !important",
              fill: "#FFFFFF !important",
              fontSize: "16px",
              "& path": {
                fill: "#FFFFFF !important",
              },
            }} 
          />
        </Box>
      </Box>
      <Box sx={{ flex: 1 }}>{message}</Box>
    </Box>
  );
});

CustomSuccessContent.displayName = "CustomSuccessContent";

const CustomErrorContent = React.forwardRef<HTMLDivElement, any>((props, ref) => {
  const { message, ...rest } = props;
  return (
    <Box
      ref={ref}
      sx={{
        borderRadius: "12px",
        backgroundColor: "#FFFFFF",
        borderBottom: "6px solid #C02B2B",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        fontSize: "16px",
        fontWeight: 500,
        color: "#212121",
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#FBEAEA",
          borderRadius: "50%",
          padding: "3px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "28px",
          height: "28px",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            backgroundColor: "#C02B2B",
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "22px",
            height: "22px",
          }}
        >
          <CloseIcon 
            sx={{ 
              color: "#FFFFFF !important",
              fill: "#FFFFFF !important",
              fontSize: "16px",
              "& path": {
                fill: "#FFFFFF !important",
              },
            }} 
          />
        </Box>
      </Box>
      <Box sx={{ flex: 1 }}>{message}</Box>
    </Box>
  );
});

CustomErrorContent.displayName = "CustomErrorContent";

const AppSnackBar: React.FunctionComponent<ReactChildren> = ({ children }) => {
  return (
    <SnackbarProvider
      Components={{
        success: CustomSuccessContent,
        error: CustomErrorContent,
        info: StyledMaterialDesignContent,
        default: StyledMaterialDesignContent,
        warning: StyledMaterialDesignContent,
      }}
      anchorOrigin={{ horizontal: "center", vertical: "top" }}
    >
      {children}
    </SnackbarProvider>
  );
};

export default AppSnackBar;
